import { type DocumentMatch, findInDocument, replaceMatches } from "@cva/shared";
import { CaseSensitive, ChevronDown, ChevronUp, QrCode, Replace, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { useEditor, useEditorApi } from "./store";
import { IconButton } from "./ui";

/**
 * Suchen & Ersetzen (⌘F / ⌘⌥F) over all pages of a design: texts and QR code
 * values. Replacing keeps the text formatting (see `replaceTextRanges`);
 * „Alle ersetzen“ is one undo step and reaches others like any edit.
 */

export const FIND_EVENT = "kaleido-find-replace";

/** Opens the panel from elsewhere (menus). */
export function openFindReplace(replace = false) {
  window.dispatchEvent(new CustomEvent(FIND_EVENT, { detail: { replace } }));
}

export function FindReplace() {
  const store = useEditorApi();
  const doc = useEditor((s) => s.doc);
  const readOnly = useEditor((s) => s.readOnly);
  const [open, setOpen] = useState(false);
  const [showReplace, setShowReplace] = useState(false);
  const [query, setQuery] = useState("");
  const [replacement, setReplacement] = useState("");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [qr, setQr] = useState(true);
  const [current, setCurrent] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const replaceRef = useRef<HTMLInputElement>(null);

  const matches = useMemo(
    () => (open ? findInDocument(doc, query, { caseSensitive, qr }) : []),
    [open, doc, query, caseSensitive, qr],
  );
  const index = matches.length === 0 ? -1 : Math.min(current, matches.length - 1);
  const match = index >= 0 ? matches[index] : undefined;

  // ⌘F opens the search, ⌘⌥F search and replace.
  useEffect(() => {
    const show = (replace: boolean) => {
      setOpen(true);
      if (replace) setShowReplace(true);
      const selected = window.getSelection()?.toString().trim();
      if (selected && selected.length <= 100 && !selected.includes("\n")) setQuery(selected);
      requestAnimationFrame(() => {
        const target = replace ? replaceRef.current : inputRef.current;
        target?.focus();
        target?.select();
      });
    };
    const onKey = (event: KeyboardEvent) => {
      const mod = event.metaKey || event.ctrlKey;
      if (!mod || event.shiftKey) return;
      if (event.code !== "KeyF" && event.key.toLowerCase() !== "f") return;
      if (document.querySelector("[role=dialog]")) return;
      event.preventDefault();
      show(event.altKey);
    };
    const onOpen = (event: Event) => show(Boolean((event as CustomEvent).detail?.replace));
    window.addEventListener("keydown", onKey);
    window.addEventListener(FIND_EVENT, onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(FIND_EVENT, onOpen);
    };
  }, []);

  const reveal = (target: DocumentMatch | undefined) => {
    if (!target) return;
    const state = store.getState();
    if (state.editingTextId && state.editingTextId !== target.elementId) {
      state.setEditingText(null);
    }
    if (state.pageIndex !== target.pageIndex) state.goToPage(target.pageIndex);
    store.getState().select([target.elementId]);
  };

  const step = (direction: 1 | -1) => {
    if (matches.length === 0) return;
    const next = (index + direction + matches.length) % matches.length;
    setCurrent(next);
    reveal(matches[next]);
  };

  const replaceCurrent = () => {
    if (!match || readOnly) return;
    store.getState().setEditingText(null);
    let replaced = 0;
    store.getState().update((draft) => {
      replaced = replaceMatches(draft, [match], replacement);
    });
    if (replaced === 0) toast.error("Ersetzen nicht möglich – der Text würde zu lang");
    // The following match moves into the current position; show it.
    const after = findInDocument(store.getState().doc, query, { caseSensitive, qr });
    const next = after.length === 0 ? undefined : after[Math.min(index, after.length - 1)];
    reveal(next);
  };

  const replaceAll = () => {
    if (matches.length === 0 || readOnly) return;
    store.getState().setEditingText(null);
    let replaced = 0;
    store.getState().update((draft) => {
      replaced = replaceMatches(draft, matches, replacement);
    });
    if (replaced === 0) toast.error("Nichts ersetzt – die Texte würden zu lang");
    else toast.success(replaced === 1 ? "1 Stelle ersetzt" : `${replaced} Stellen ersetzt`);
  };

  const close = () => {
    setOpen(false);
  };

  if (!open) return null;
  const pages = new Set(matches.map((entry) => entry.pageIndex)).size;
  return (
    <div className="absolute top-3 right-3 z-30 w-80 rounded-xl border border-zinc-200 bg-white p-2 shadow-lg">
      <div className="flex items-center gap-1">
        {!readOnly ? (
          <IconButton
            label={showReplace ? "Ersetzen ausblenden" : "Ersetzen einblenden"}
            active={showReplace}
            onClick={() => setShowReplace(!showReplace)}
          >
            <Replace />
          </IconButton>
        ) : null}
        <div className="relative min-w-0 flex-1">
          <input
            ref={inputRef}
            value={query}
            placeholder="Suchen"
            aria-label="Suchen"
            onChange={(event) => {
              setQuery(event.target.value);
              setCurrent(0);
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") close();
              if (event.key === "Enter") {
                event.preventDefault();
                step(event.shiftKey ? -1 : 1);
              }
            }}
            className="h-8 w-full rounded-md border border-zinc-200 pr-14 pl-2 text-sm focus:border-brand-500 focus:outline-none"
          />
          <span className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 text-[11px] text-zinc-400 tabular-nums">
            {query ? (matches.length > 0 ? `${index + 1} / ${matches.length}` : "0 / 0") : ""}
          </span>
        </div>
        <IconButton
          label="Groß-/Kleinschreibung beachten"
          active={caseSensitive}
          onClick={() => setCaseSensitive(!caseSensitive)}
        >
          <CaseSensitive />
        </IconButton>
        <IconButton label="Vorheriger Treffer" shortcut="⇧↵" onClick={() => step(-1)}>
          <ChevronUp />
        </IconButton>
        <IconButton label="Nächster Treffer" shortcut="↵" onClick={() => step(1)}>
          <ChevronDown />
        </IconButton>
        <IconButton label="Schließen" shortcut="Esc" onClick={close}>
          <X />
        </IconButton>
      </div>
      {showReplace && !readOnly ? (
        <div className="mt-1.5 flex items-center gap-1 pl-9">
          <input
            ref={replaceRef}
            value={replacement}
            placeholder="Ersetzen durch"
            aria-label="Ersetzen durch"
            onChange={(event) => setReplacement(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") close();
              if (event.key === "Enter") {
                event.preventDefault();
                if (event.metaKey || event.ctrlKey) replaceAll();
                else replaceCurrent();
              }
            }}
            className="h-8 min-w-0 flex-1 rounded-md border border-zinc-200 px-2 text-sm focus:border-brand-500 focus:outline-none"
          />
          <button
            type="button"
            disabled={!match}
            onClick={replaceCurrent}
            className="h-8 rounded-md border border-zinc-200 px-2 text-xs text-zinc-700 hover:bg-zinc-50 disabled:opacity-40"
          >
            Ersetzen
          </button>
          <button
            type="button"
            disabled={matches.length === 0}
            onClick={replaceAll}
            title="Alle ersetzen (⌘↵)"
            className="h-8 rounded-md bg-brand-600 px-2 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-40"
          >
            Alle
          </button>
        </div>
      ) : null}
      <div className="mt-1.5 flex items-center gap-2 px-1 text-[11px] text-zinc-500">
        <span className="min-w-0 flex-1 truncate">
          {!query
            ? "Texte und QR-Codes auf allen Seiten"
            : matches.length === 0
              ? "Keine Treffer"
              : `${matches.length} Treffer${
                  doc.pages.length > 1 ? ` auf ${pages} ${pages === 1 ? "Seite" : "Seiten"}` : ""
                }${match?.field === "qr" ? " · aktueller Treffer: QR-Code" : ""}`}
        </span>
        <button
          type="button"
          title="QR-Code-Inhalte einbeziehen"
          aria-pressed={qr}
          onClick={() => setQr(!qr)}
          className={cn(
            "inline-flex items-center gap-1 rounded px-1.5 py-0.5 [&_svg]:size-3",
            qr ? "bg-brand-50 text-brand-700" : "text-zinc-500 hover:bg-zinc-100",
          )}
        >
          <QrCode /> QR
        </button>
      </div>
    </div>
  );
}
